import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Chapter, Game, GameModule, GameProgress, ModuleResult, Reward } from '../types/game';
import { ProgressService } from '../services/progressService';

export type GamePhase = 'module' | 'reward' | 'chapter' | 'final';

interface GameEngineValue {
  game: Game;
  progress: GameProgress;
  currentChapter: Chapter;
  currentModule: GameModule;
  phase: GamePhase;
  activeReward: Reward | null;
  transitionChapter: Chapter | null;
  completeModule: (result: ModuleResult) => void;
  continuePhase: () => void;
  resetGame: () => void;
}

const GameContext = createContext<GameEngineValue | null>(null);

function allRequiredCompleted(game: Game, progress: GameProgress): boolean {
  return game.chapters.every((chapter) => chapter.modules.every((module) => !module.required || progress.completedModules.includes(module.id)));
}

function resolvePosition(game: Game, progress: GameProgress): { chapter: Chapter; module: GameModule } {
  const chapter = game.chapters.find((item) => item.id === progress.currentChapterId) ?? game.chapters[0];
  const module = chapter?.modules.find((item) => item.id === progress.currentModuleId) ?? chapter?.modules[0];
  if (!chapter || !module) throw new Error('El juego no tiene un flujo jugable.');
  return { chapter, module };
}

export function GameProvider({ game, children }: { game: Game; children: React.ReactNode }) {
  const [progress, setProgress] = useState(() => ProgressService.load(game) ?? ProgressService.create(game));
  const [phase, setPhase] = useState<GamePhase>(() => allRequiredCompleted(game, progress) ? 'final' : 'module');
  const [pendingPhase, setPendingPhase] = useState<GamePhase>('module');
  const [activeReward, setActiveReward] = useState<Reward | null>(null);
  const [transitionChapter, setTransitionChapter] = useState<Chapter | null>(null);
  const { chapter: currentChapter, module: currentModule } = resolvePosition(game, progress);

  const completeModule = useCallback((result: ModuleResult) => {
    if (!result.success || progress.completedModules.includes(currentModule.id)) return;
    let next = ProgressService.complete(progress, currentModule.id, result, currentModule.reward);
    const chapterIndex = game.chapters.findIndex((chapter) => chapter.id === currentChapter.id);
    const moduleIndex = currentChapter.modules.findIndex((module) => module.id === currentModule.id);
    const nextModule = currentChapter.modules[moduleIndex + 1];
    const nextChapter = game.chapters[chapterIndex + 1];
    let destination: GamePhase = 'final';

    if (nextModule) {
      next = { ...next, currentModuleId: nextModule.id };
      destination = 'module';
    } else if (nextChapter?.modules[0]) {
      next = { ...next, currentChapterId: nextChapter.id, currentModuleId: nextChapter.modules[0].id };
      setTransitionChapter(nextChapter);
      destination = 'chapter';
    }

    ProgressService.save(next);
    setProgress(next);
    setPendingPhase(destination);
    setActiveReward(currentModule.reward);
    setPhase(currentModule.reward ? 'reward' : destination);
  }, [currentChapter, currentModule, game.chapters, progress]);

  const continuePhase = useCallback(() => {
    if (phase === 'reward') setPhase(pendingPhase);
    else if (phase === 'chapter') setPhase('module');
  }, [pendingPhase, phase]);

  const resetGame = useCallback(() => {
    ProgressService.reset(game.id);
    const fresh = ProgressService.create(game);
    ProgressService.save(fresh);
    setProgress(fresh);
    setPhase('module');
    setPendingPhase('module');
    setActiveReward(null);
    setTransitionChapter(null);
  }, [game]);

  const value = useMemo(() => ({ game, progress, currentChapter, currentModule, phase, activeReward, transitionChapter, completeModule, continuePhase, resetGame }),
    [activeReward, completeModule, continuePhase, currentChapter, currentModule, game, phase, progress, resetGame, transitionChapter]);

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

// The hook intentionally shares this module with its provider to keep the context private.
// eslint-disable-next-line react-refresh/only-export-components
export function useGameEngine(): GameEngineValue {
  const context = useContext(GameContext);
  if (!context) throw new Error('useGameEngine debe usarse dentro de GameProvider.');
  return context;
}
